import { Component, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { LoggedInUserSettings, ISelectedContract } from 'src/app/core/services/settings/logged-in-user-settings';
import { AccountService } from 'src/app/core/services/account.service';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { OktaAuthStateService } from '@okta/okta-angular';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { ADPResourceService } from 'digital-core';
import { PasswordFormControls } from '../../password/password-form/password-form.component';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-password-settings',
  templateUrl: './password-settings.component.html',
  styleUrls: ['./password-settings.component.scss'],
})
export class PasswordSettingsComponent implements OnInit, OnDestroy {
  @Output() public emitNextEmailFlow: EventEmitter<boolean> = new EventEmitter(false);

  passwordForm = new FormGroup<PasswordFormControls>({});
  showCurrentPassword = false;
  emailAddress: string;
  isLoading: boolean;
  private destroy$ = new Subject<boolean>();

  constructor(
    public dialogRef: MatDialogRef<PasswordSettingsComponent>,
    public oktaAuth: OktaAuthStateService,
    public resourceService: ADPResourceService,
    private snackbarService: SnackbarService,
    private loggedInUserSettings: LoggedInUserSettings,
    private accountService: AccountService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.router.url.endsWith('/change-password')) {
      this.showCurrentPassword = true;
    }
    const joined$ = combineLatest([this.loggedInUserSettings.$selectedContract, this.loggedInUserSettings.$userClaims]).pipe(
      takeUntil(this.destroy$),
      filter(([a, b]) => a !== null && b !== null)
    );

    joined$.subscribe(([selectedContract, userClaims]: [ISelectedContract, UserClaims]) => {
      this.emailAddress = userClaims.email;
    });
  }

  public updatePassword(): void {
    this.isLoading = true;
    if (this.passwordForm.invalid) {
      return;
    }

    this.accountService
      .postUpdatePassword(this.emailAddress, this.passwordForm.controls.currentPassword?.value, this.passwordForm.controls.password?.value)
      .subscribe({
        next: () => {
          this.snackbarService.showSimpleMessage(this.resourceService.getViewLabel('Account_Change_Password_Success_Message'));
          this.passwordForm.reset();
          this.isLoading = false;
        },
        error: (err) => {
          if (err.error && err.error.Message) {
            if (err.error && err.error.ErrorCode == 'InvalidRequest') {
              err.error.Message = this.resourceService.getViewLabel('PasswordForm_Current_New_Password_Match_Error');
            } else if (err.error.Message.indexOf('at Okta.Sdk.Internal') > -1) {
              err.error.Message = err.error.Message.substring(0, err.error.Message.indexOf('at Okta.Sdk.Internal')).trim();
              if (
                err.error.Message.toLowerCase().indexOf('update of credentials failed (403, e0000014):') > -1 &&
                err.error.Message.length > 80
              ) {
                err.error.Message = this.resourceService.getViewLabel('Global_Error_Code_Password_ece0000014');
              } else {
                err.error.Message = err.error.Message.substring(err.error.Message.indexOf(':') + 1).trim();
              }
            }
            err.error.CustomTitle = err.error.Message;
          }
          this.isLoading = false;
          throw err;
        },
      });
  }

  nextScreen() {
    this.emitNextEmailFlow.emit(false);
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  getViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
